import { useContext, useEffect } from 'react';

import { base64, hex } from '@scure/base';
import { Transaction } from '@scure/btc-signer';
import axios from 'axios';
import { create } from 'zustand';

import { BtcLikeWallet, SignPsbtOptions } from '@/networks/btc-like/common/types';

import { convertSignInputsToInputType } from '../../common';
import { NETWORK } from '../constants';
import { DogeContext } from '../providers/DogeProvider';
import { SignPsbtDogeOptions } from '../providers/types';

type CurrentAccountStore = {
  address: string | null;
  publicKey: string | null;
  setAccount: (address: string | null, publicKey: string | null) => void;
};

export const useCurrentAddressStore = create<CurrentAccountStore>((set) => {
  return {
    address: null,
    publicKey: null,
    setAccount: (address, publicKey) => {
      set({ address, publicKey });
    },
  };
});

export const useDogeWallet = (): BtcLikeWallet => {
  const ctx = useContext(DogeContext);

  if (!ctx) {
    throw new Error('useDogeWallet must be used within DogeProvider');
  }

  const { address, publicKey, setAccount } = useCurrentAddressStore();
  const { address: walletAddress, connect, disconnect, getPublicKey, isConnected } = ctx;

  useEffect(() => {
    if (walletAddress) {
      (async () => {
        const publicKey = await getPublicKey();
        if (!publicKey) {
          throw new Error('Public key not found');
        }

        setAccount(walletAddress, publicKey);
      })().catch((error) => {
        console.error(error);
      });
    }
  }, [walletAddress]);

  const signPsbt = async (
    psbtBase64: string,
    { broadcast, allowedSignHash, finalizeSignalInput, sighTx }: SignPsbtOptions
  ) => {
    if (!address) {
      throw new Error('Wallet is not connected');
    }

    const bytePsbt = base64.decode(psbtBase64);

    let hexPsbt: string;

    if (finalizeSignalInput) {
      const txInitial = Transaction.fromPSBT(bytePsbt, {
        allowUnknownOutputs: true,
        disableScriptCheck: true,
        allowLegacyWitnessUtxo: true,
      });

      txInitial.finalizeIdx(0);

      hexPsbt = hex.encode(txInitial.toPSBT(0));
    } else {
      hexPsbt = hex.encode(bytePsbt);
    }

    const options: SignPsbtDogeOptions = {
      autoFinalized: finalizeSignalInput,
      toSignInputs: convertSignInputsToInputType({ [address]: sighTx }, allowedSignHash, true),
    };

    const res = await ctx.signPsbt(hexPsbt, options);

    if (!broadcast) {
      return {
        signedBase64Psbt: res,
        txId: null,
      };
    }

    const embedUrl = NETWORK.MempoolApiEndpoint.concat('/tx');

    const response = await axios.post<string>(embedUrl, res);

    return {
      signedBase64Psbt: res,
      txId: response.data,
    };
  };

  return {
    isConnected,
    availableAddresses: ctx.address ? [ctx.address] : [],
    address,
    publicKey,
    setAddress: () => null,
    disconnect,
    connect,
    signPsbt,
  };
};
